/**
 * nco-pdp-variants.js — Run 5.2 / T5 selector update
 *
 * Variant state manager for the PDP. Manages colour swatch selection, mount
 * type, finish, size, tone mode, second colour, tray colour, overflow, and
 * tap-hole selectors. Emits `nco:variant:change` events consumed by the
 * gallery (5.1) and other PDP components.
 *
 * On colour selection:
 *   1. Gallery image swap (via nco:variant:change → 5.1)
 *   2. Price update
 *   3. Availability + lead-time update
 *   4. Colour-specific content load from ColourContent metaobject
 *   5. Quick-fact chip refresh
 *   6. Shopify variant ID sync for add-to-cart
 *
 * Loaded as an ES module via:
 *   <script src="{{ 'nco-pdp-variants.js' | asset_url }}" type="module" defer></script>
 *
 * Standards: S2 (vanilla JS, no jQuery, event delegation), S5 (ARIA updates),
 *            S6 (deferred load).
 */

/* ------------------------------------------------------------------ */
/* Selectors                                                           */
/* ------------------------------------------------------------------ */

const SEL = {
  root: '[data-nco-pdp-variants]',
  variantData: '#nco-variant-data',
  colourContent: '#nco-colour-content',
  specSheetMap: '#nco-spec-sheet-map',
  specRegistry: '#nco-spec-registry',
  installGuideMap: '#nco-install-guide-map',
  variantIndex: '#nco-variant-index',
  colourImageMap: '#nco-colour-image-map',
  colourHexMap: '#nco-colour-hex-map',
  hvMeta: '#nco-hv-meta',
  swatchHexTarget: '[data-nco-hexkey]',
  installGuideItem: '[data-nco-install-guide]',
  colourSwatch: '[data-nco-colour-swatch]',
  colourLabel: '[data-nco-colour-label]',
  mountOption: '[data-nco-mount-option]',
  finishOption: '[data-nco-finish-option]',
  sizeOption: '[data-nco-size-option]',
  toneOption: '[data-nco-tone-option]',
  secondaryColourOption: '[data-nco-secondary-colour-option]',
  secondaryColourLabel: '[data-nco-secondary-colour-label]',
  secondaryColourGroup: '[data-nco-variant-group="secondaryColour"]',
  trayColourOption: '[data-nco-tray-colour-option]',
  trayColourLabel: '[data-nco-tray-colour-label]',
  overflowOption: '[data-nco-overflow-option]',
  overflowFinishOption: '[data-nco-overflow-finish-option]',
  overflowFinishGroup: '[data-nco-variant-group="overflowFinish"]',
  overflowFinishLabel: '[data-nco-overflow-finish-label]',
  tapholeOption: '[data-nco-taphole-option]',
  addToCartBtn: '[data-nco-add-to-cart], form[data-product-form] button[type="submit"][name="add"], [data-add-to-cart]',
  unavailableMsg: '[data-nco-unavailable-msg]',
  productTitle: '[data-nco-product-title]',
  productDescription: '[data-nco-product-description]',
  specSheetLink: '[data-nco-spec-sheet-btn], [data-nco-spec-sheet-link]',
  specSheetListItem: '[data-nco-spec-sheet-list-item]',
  specSheetUnavailable: '[data-nco-spec-sheet-unavailable]',
  pricePrefix: '[data-nco-price-prefix]',
  priceValue: '[data-nco-price-value]',
  priceCompare: '[data-nco-price-compare]',
  availabilityStatus: '[data-nco-availability-status]',
  availabilityLead: '[data-nco-availability-lead]',
  colourContentDisplay: '[data-nco-colour-content-display]',
  sku: '.product-sku',
  variantIdInput: '[data-nco-variant-id]',
  chips: '[data-nco-pdp-chips]',
  chipValue: '[data-chip-value]',
};

const CSS = {
  swatchActive: 'nco-pdp-variants__swatch--active',
  optionActive: 'nco-pdp-variants__option-btn--active',
};

/* ------------------------------------------------------------------ */
/* State                                                               */
/* ------------------------------------------------------------------ */

let rootEl = null;
let variantStore = null;
let colourContentStore = null;
let specSheetMap = null;
/* Spec sheet resolution decoupled from the 250-variant + ~20 metaobject bracket caps:
   specSheetMap maps variantId -> registry handle (+ a "default" handle); specRegistry maps
   registry handle -> spec-sheet URL (built from product_colours-style .values, uncapped). */
let specRegistry = null;
/* True only during the first synchronous init() pass. While set, we seed the form's
   variant id / price / SKU from the default variant but DO NOT swap the gallery image:
   the page must stay on the server-rendered hero carousel image on load. The first real
   user selection clears this and resumes normal image swapping. */
let initialLoad = true;
/* Complete colour→hex map ({ "<handle>": "#rrggbb" }) for ALL 29 colours + 4 overflow
   finishes, rendered server-side from product_colours.values (uncapped). Single source
   used to paint every dot: colour swatches, tray swatches, overflow-finish swatches.
   Replaces the capped `shop.metaobjects.product_colours[handle]` Liquid bracket lookup. */
let colourHexMap = null;
let productTitleFallback = '';
let productDescriptionFallback = '';
/* High-variant (>250) resolution data — present only when custom.variant_index exists.
   For cylinders/shelf/loop, Liquid's 250-variant cap means the array above is incomplete,
   so resolution comes from this server-rendered index instead. */
let hvIndex = null;        /* { "<opt1>|<opt2>|...": {id, price, sku} } */
let hvColourImg = null;    /* { "<colourHandle>": {src, alt} } */
let hvOptionNames = null;  /* canonical option order, e.g. ["Colour","Mount","Configuration"] */
let hvConfigMap = null;    /* { "<tapHole>|<overflow>|<finish>": "<Configuration value>" } */
const state = {
  colour: '',
  mount: '',
  finish: '',
  sizeLabel: '',
  toneMode: '',
  secondaryColour: '',
  trayColour: '',
  overflow: '',
  overflowFinish: '',
  tapHole: '',
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function parseJSON(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (_) {
    return null;
  }
}

function findVariantById(id) {
  if (!variantStore || !variantStore.variants) return null;
  for (let i = 0; i < variantStore.variants.length; i++) {
    if (variantStore.variants[i].id === id) return variantStore.variants[i];
  }
  return null;
}

/* ---- High-variant index resolution (>250 variants; no Storefront token) ---- */
function ncoHandleize(s) {
  return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
function hvValueForOption(name) {
  const n = String(name || '').toLowerCase();
  if (n === 'colour' || n === 'color') return state.colour;
  if (n === 'mount') return state.mount;
  if (n === 'tray' || n === 'tray colour') return state.trayColour;
  if (n === 'finish') return state.finish;
  if (n === 'size') return state.sizeLabel;
  if (n === 'tone' || n === 'tone mode') return state.toneMode;
  if (n === 'configuration') {
    if (!hvConfigMap) return '';
    return hvConfigMap[state.tapHole + '|' + state.overflow + '|' + state.overflowFinish] || '';
  }
  return '';
}
/* Build the index key from the current selection (option order = hvOptionNames) and
   look it up. Returns a variant-shaped object, or null if the combo is incomplete or
   does not exist (the latter handles cylinders' partial colour x tray matrix). */
function resolveVariantFromIndex() {
  if (!hvIndex || !hvOptionNames) return null;
  const parts = [];
  for (let i = 0; i < hvOptionNames.length; i++) {
    const val = hvValueForOption(hvOptionNames[i]);
    if (!val) return null;
    parts.push(val);
  }
  const entry = hvIndex[parts.join('|')];
  if (!entry) return null;
  const cents = Math.round(parseFloat(entry.price) * 100);
  const img = (hvColourImg && state.colour) ? hvColourImg[ncoHandleize(state.colour)] : null;
  return {
    id: entry.id,
    sku: entry.sku || '',
    price: cents,
    priceDisplay: formatMoney(cents),
    compareAtPriceDisplay: '',
    colourName: state.colour,
    available: true,
    _hvImage: img,
  };
}
/* For >250 products the capped #merged-variant-images map won't contain the resolved
   variant, so swap the main/thumb image directly from the per-colour image map. */
function updateHvImage(variant) {
  if (!variant || !variant._hvImage || !variant._hvImage.src) return;
  const main = document.querySelector('[id^="merged-variant-img-"]');
  const thumb = document.querySelector('[id^="merged-variant-thumb-"]');
  if (main) { main.src = variant._hvImage.src; main.removeAttribute('srcset'); if (variant._hvImage.alt) main.alt = variant._hvImage.alt; }
  if (thumb) { thumb.src = variant._hvImage.src; thumb.removeAttribute('srcset'); }
}

function hasColourControlValue(colourName) {
  const swatches = rootEl ? rootEl.querySelectorAll(SEL.colourSwatch) : [];
  for (let i = 0; i < swatches.length; i++) {
    if (swatches[i].getAttribute('data-colour-name') === colourName) return true;
  }
  return false;
}

function resolveTrayURLParam(trayParam) {
  if (trayParam !== 'With Tray') return trayParam;

  const buttons = rootEl.querySelectorAll(SEL.trayColourOption);
  if (!buttons.length) return trayParam;

  const selectedColourHandle = ncoHandleize(state.colour);
  let firstTray = '';

  for (let i = 0; i < buttons.length; i++) {
    const value = buttons[i].getAttribute('data-tray-colour-value') || '';
    if (!value || value === 'No Tray') continue;
    if (!firstTray) firstTray = value;

    const trayBase = value.replace(/\s+Tray$/i, '');
    if (selectedColourHandle && ncoHandleize(trayBase) === selectedColourHandle) {
      return value;
    }
  }

  return firstTray || trayParam;
}

function findVariantByColour(colourName) {
  if (!variantStore || !variantStore.variants) return null;
  const lower = colourName.toLowerCase();
  for (let i = 0; i < variantStore.variants.length; i++) {
    const vColour = (variantStore.variants[i].colourName || '').toLowerCase();
    if (vColour === lower) return variantStore.variants[i];
  }
  return null;
}

function formatMoney(cents) {
  if (!variantStore) return '';
  const fmt = variantStore.moneyFormat || '${{amount}}';
  const amount = (cents / 100).toFixed(2);
  const amountNoDecimals = Math.floor(cents / 100).toString();
  const amountWithComma = amount.replace('.', ',');
  const amountNoDecimalsWithComma = amountNoDecimals;

  return fmt
    .replace('{{amount_with_comma_separator}}', amountWithComma)
    .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
    .replace('{{amount_no_decimals}}', amountNoDecimals)
    .replace('{{amount}}', amount);
}

function getURLParam(name) {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || '';
}

function escapeHTML(str) {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(str));
  return div.innerHTML;
}

/* ------------------------------------------------------------------ */
/* Variant resolution                                                  */
/* ------------------------------------------------------------------ */

function filterByField(arr, field, value) {
  const result = [];
  for (let i = 0; i < arr.length; i++) {
    const fieldVal = arr[i][field] || '';
    if (String(fieldVal).toLowerCase() === String(value).toLowerCase()) {
      result.push(arr[i]);
    }
  }
  return result;
}

function filterByTitle(arr, value) {
  const result = [];
  const needle = String(value || '').toLowerCase();
  for (let i = 0; i < arr.length; i++) {
    const title = String(arr[i].title || '').toLowerCase();
    if (title.indexOf(needle) !== -1) {
      result.push(arr[i]);
    }
  }
  return result;
}

/**
 * Find the best matching variant for current state. Colour is the primary
 * discriminator; mount, overflow, and tap hole narrow further.
 */
function resolveVariant() {
  if (hvIndex) return resolveVariantFromIndex();
  const variants = variantStore.variants;
  let candidates = [];

  /* Filter by colour first */
  for (let i = 0; i < variants.length; i++) {
    if (state.colour && variants[i].colourName !== state.colour) continue;
    candidates.push(variants[i]);
  }

  if (candidates.length === 0) {
    return null;
  }

  /* Narrow by mount */
  if (state.mount) {
    candidates = filterByTitle(candidates, state.mount);
    if (candidates.length === 0) return null;
  }

  /* Narrow by finish */
  if (state.finish) {
    candidates = filterByField(candidates, 'finish', state.finish);
    if (candidates.length === 0) return null;
  }

  /* Narrow by size */
  if (state.sizeLabel) {
    candidates = filterByField(candidates, 'sizeLabel', state.sizeLabel);
    if (candidates.length === 0) return null;
  }

  /* Narrow by tone mode */
  if (state.toneMode) {
    candidates = filterByField(candidates, 'toneMode', state.toneMode);
    if (candidates.length === 0) return null;
  }

  /* Narrow by secondary colour */
  if (state.secondaryColour) {
    candidates = filterByField(candidates, 'secondaryColourName', state.secondaryColour);
    if (candidates.length === 0) return null;
  }

  /* Narrow by tray colour */
  if (state.trayColour) {
    candidates = filterByField(candidates, 'trayColourName', state.trayColour);
    if (candidates.length === 0) return null;
  }

  /* Narrow by overflow */
  if (state.overflow) {
    candidates = filterByField(candidates, 'overflow', state.overflow);
    if (candidates.length === 0) return null;
  }

  /* Narrow by overflow finish */
  if (state.overflowFinish) {
    candidates = filterByField(candidates, 'overflowFinish', state.overflowFinish);
    if (candidates.length === 0) return null;
  }

  /* Narrow by tap hole */
  if (state.tapHole) {
    candidates = filterByField(candidates, 'tapHole', state.tapHole);
    if (candidates.length === 0) return null;
  }

  return candidates[0] || null;
}

function hasVariantForSelection(overrides) {
  const previous = { ...state };
  state.colour = Object.prototype.hasOwnProperty.call(overrides, 'colour') ? overrides.colour : state.colour;
  state.mount = Object.prototype.hasOwnProperty.call(overrides, 'mount') ? overrides.mount : state.mount;
  state.finish = Object.prototype.hasOwnProperty.call(overrides, 'finish') ? overrides.finish : state.finish;
  state.sizeLabel = Object.prototype.hasOwnProperty.call(overrides, 'sizeLabel') ? overrides.sizeLabel : state.sizeLabel;
  state.toneMode = Object.prototype.hasOwnProperty.call(overrides, 'toneMode') ? overrides.toneMode : state.toneMode;
  state.secondaryColour = Object.prototype.hasOwnProperty.call(overrides, 'secondaryColour') ? overrides.secondaryColour : state.secondaryColour;
  state.trayColour = Object.prototype.hasOwnProperty.call(overrides, 'trayColour') ? overrides.trayColour : state.trayColour;
  state.overflow = Object.prototype.hasOwnProperty.call(overrides, 'overflow') ? overrides.overflow : state.overflow;
  state.overflowFinish = Object.prototype.hasOwnProperty.call(overrides, 'overflowFinish') ? overrides.overflowFinish : state.overflowFinish;
  state.tapHole = Object.prototype.hasOwnProperty.call(overrides, 'tapHole') ? overrides.tapHole : state.tapHole;
  const match = resolveVariant();
  Object.assign(state, previous);
  return Boolean(match);
}

/* ------------------------------------------------------------------ */
/* Event emission                                                      */
/* ------------------------------------------------------------------ */

function emitVariantChange(variant) {
  document.dispatchEvent(
    new CustomEvent('nco:variant:change', {
      bubbles: true,
      detail: {
        variantId: variant.id,
        variant_id: variant.id,
        colourName: variant.colourName,
        price: variant.price,
        available: variant.available,
        /* Initial (load-time) seeding vs a real user selection. Consumers that swap
           the gallery image (or record an interaction signal) skip the initial one so
           the page stays on the hero carousel image until the customer chooses. */
        initial: initialLoad,
      },
    })
  );
}

/* ------------------------------------------------------------------ */
/* DOM updates                                                         */
/* ------------------------------------------------------------------ */

function updatePrice(variant) {
  const priceEl = document.querySelector('#product-price');
  const compareEl = document.querySelector('#product-compare-price');
  if (!priceEl) return;

  priceEl.textContent = variant.priceDisplay || formatMoney(variant.price);

  if (compareEl) {
    if (variant.compareAtPriceDisplay) {
      compareEl.textContent = variant.compareAtPriceDisplay;
      compareEl.style.display = '';
    } else {
      compareEl.textContent = '';
      compareEl.style.display = 'none';
    }
  }
}

function updateSku(variant) {
  const els = document.querySelectorAll(SEL.sku);
  for (let i = 0; i < els.length; i++) {
    els[i].textContent = variant && variant.sku ? variant.sku : '';
  }
}

function updateAvailability(variant) {
  const statusEl = document.querySelector(SEL.availabilityStatus);
  const leadEl = document.querySelector(SEL.availabilityLead);

  if (statusEl) {
    statusEl.textContent = variant.availability || '';
    statusEl.style.display = variant.availability ? '' : 'none';
  }
  if (leadEl) {
    leadEl.textContent = variant.leadTime || '';
    leadEl.style.display = variant.leadTime ? '' : 'none';
  }
}

function setChipValue(container, chipType, value) {
  const chip = container.querySelector('[data-chip-type="' + chipType + '"]');
  if (!chip) return;
  const label = chip.querySelector(SEL.chipValue);
  if (label) {
    label.textContent = value;
  }
  chip.style.display = value ? '' : 'none';
}

function buildDimensions(variant) {
  if (!variant.widthMm) return '';
  let dim = String(variant.widthMm);
  if (variant.depthMm) dim += ' x ' + variant.depthMm;
  if (variant.heightMm) dim += ' x ' + variant.heightMm;
  return dim + 'mm';
}

function updateChips(variant) {
  const chipsEl = document.querySelector(SEL.chips);
  if (!chipsEl) return;

  setChipValue(chipsEl, 'dimensions', buildDimensions(variant));
  setChipValue(chipsEl, 'lead-time', variant.leadTime || '');
  setChipValue(chipsEl, 'overflow', variant.overflow || '');
  setChipValue(chipsEl, 'waste', variant.waste || '');
}

function updateVariantId(variant) {
  const inputs = document.querySelectorAll(SEL.variantIdInput);
  for (let i = 0; i < inputs.length; i++) {
    inputs[i].value = variant.id;
  }

  /* Also update any native Shopify variant selects */
  const nativeSelect = document.querySelector(
    'select[name="id"], input[name="id"]:not(' + SEL.variantIdInput + ')'
  );
  if (nativeSelect) {
    nativeSelect.value = variant.id;
  }

  /* Bridge to the merged-template gallery: product-image.liquid swaps the main
     image on a bubbling `change` of input[name="id"]. A programmatic `.value=`
     assignment does NOT fire `change`, so dispatch it here.
     Skip on initial load: the `.value=` above already seeds the add-to-cart id, but
     firing `change` would make product-image.liquid slickGoTo(0) onto the default
     colour image instead of leaving the hero carousel showing. */
  if (!initialLoad) {
    const idInputs = document.querySelectorAll('input[name="id"], select[name="id"]');
    for (let i = 0; i < idInputs.length; i++) {
      idInputs[i].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* High-variant products: the capped gallery image map won't have this variant, so
     swap the image from the per-colour map directly. Also skipped on initial load. */
  if (variant && variant._hvImage && !initialLoad) updateHvImage(variant);
}

function loadColourContent(colourName) {
  const display = document.querySelector(SEL.colourContentDisplay);
  if (!display || !colourContentStore) return;

  /* Find content by colour name or handle (lowercased, hyphenated) */
  const handle = colourName.toLowerCase().replace(/\s+/g, '-');
  let content =
    colourContentStore[handle] || colourContentStore[colourName] || null;

  if (!content) {
    /* Try case-insensitive search */
    const keys = Object.keys(colourContentStore);
    for (let i = 0; i < keys.length; i++) {
      const entry = colourContentStore[keys[i]];
      if (
        entry.colourName &&
        entry.colourName.toLowerCase() === colourName.toLowerCase()
      ) {
        content = entry;
        break;
      }
    }
  }

  if (!content) {
    display.innerHTML = '';
    return;
  }

  let html = '';
  if (content.undertone) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Undertone</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.undertone) +
      '</span></div>';
  }
  if (content.lightBehaviour) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Light behaviour</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.lightBehaviour) +
      '</span></div>';
  }
  if (content.pairings) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Pairs with</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.pairings) +
      '</span></div>';
  }
  if (content.styleMatch) {
    html +=
      '<div class="nco-pdp-variants__colour-detail">' +
      '<span class="nco-pdp-variants__colour-detail-label">Style match</span>' +
      '<span class="nco-pdp-variants__colour-detail-value">' +
      escapeHTML(content.styleMatch) +
      '</span></div>';
  }

  display.innerHTML = html;
}

function updateOptionButtons(selector, dataAttr, activeValue) {
  const buttons = rootEl.querySelectorAll(selector);
  for (let i = 0; i < buttons.length; i++) {
    const isMatch = buttons[i].getAttribute(dataAttr) === activeValue;
    /* Tray + overflow-finish render as dot swatches; everything else as text buttons.
       Toggle the active modifier that matches the button's base class so the correct
       visual state shows for each. */
    const activeClass = buttons[i].classList.contains('nco-pdp-variants__swatch')
      ? CSS.swatchActive
      : CSS.optionActive;
    buttons[i].classList.toggle(activeClass, isMatch);
    buttons[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }
}

/* Paint every dot ([data-nco-hexkey]) from the complete colour→hex map. Covers colour
   swatches, tray swatches, and overflow-finish swatches with one source of truth, so no
   dot can inherit a wrong colour from a capped metaobject lookup. */
function applySwatchHexes() {
  if (!colourHexMap) return;
  const dots = rootEl.querySelectorAll(SEL.swatchHexTarget);
  for (let i = 0; i < dots.length; i++) {
    const key = dots[i].getAttribute('data-nco-hexkey') || '';
    const hex = colourHexMap[key];
    if (hex) dots[i].style.backgroundColor = hex;
  }
}

function setButtonAvailability(button, available) {
  button.disabled = !available;
  button.setAttribute('aria-disabled', available ? 'false' : 'true');
  button.hidden = !available;
}

function hasAnyAvailableVariantForColour(colourName) {
  if (hvIndex && hvOptionNames) {
    let ci = hvOptionNames.indexOf('Colour');
    if (ci < 0) ci = 0;
    for (const k in hvIndex) { if (k.split('|')[ci] === colourName) return true; }
    return false;
  }
  for (let i = 0; i < variantStore.variants.length; i++) {
    if (variantStore.variants[i].colourName === colourName && variantStore.variants[i].available) {
      return true;
    }
  }
  return false;
}

function updateOptionAvailability() {
  const optionGroups = [
    [SEL.colourSwatch, 'data-colour-name', 'colour'],
    [SEL.mountOption, 'data-mount-value', 'mount'],
    [SEL.finishOption, 'data-finish-value', 'finish'],
    [SEL.sizeOption, 'data-size-value', 'sizeLabel'],
    [SEL.toneOption, 'data-tone-value', 'toneMode'],
    [SEL.secondaryColourOption, 'data-secondary-colour-value', 'secondaryColour'],
    [SEL.trayColourOption, 'data-tray-colour-value', 'trayColour'],
    [SEL.overflowOption, 'data-overflow-value', 'overflow'],
    [SEL.overflowFinishOption, 'data-overflow-finish-value', 'overflowFinish'],
    [SEL.tapholeOption, 'data-taphole-value', 'tapHole'],
  ];

  for (let i = 0; i < optionGroups.length; i++) {
    const [selector, attr, stateKey] = optionGroups[i];
    /* Index path: only colour and tray can be a partial matrix (cylinders are 702 of 29x30).
       The Configuration facets (tap hole / overflow state / overflow finish) and mount are
       full-matrix for shelf/loop, and the overflow<->finish coupling is managed by
       selectOverflow/selectOverflowFinish — greying them here would incorrectly hide a valid
       state (e.g. "Overflow" before a finish is chosen builds an incomplete config). Skip them. */
    if (hvIndex && stateKey !== 'colour' && stateKey !== 'trayColour') continue;
    const buttons = rootEl.querySelectorAll(selector);
    let visibleCount = 0;
    let lastVisibleButton = null;
    for (let j = 0; j < buttons.length; j++) {
      const value = buttons[j].getAttribute(attr) || '';
      const available = stateKey === 'colour'
        ? hasAnyAvailableVariantForColour(value)
        : hasVariantForSelection({ [stateKey]: value });
      setButtonAvailability(buttons[j], available);
      if (available) {
        visibleCount++;
        lastVisibleButton = buttons[j];
      }
    }

    if (visibleCount === 1 && lastVisibleButton && !lastVisibleButton.classList.contains(CSS.optionActive)) {
      lastVisibleButton.click();
    }

    const group = buttons.length > 0 ? buttons[0].closest('[data-nco-variant-group]') : null;
    if (group) {
      group.hidden = visibleCount === 0;
    }
  }
}

function updateSpecSheet(variant) {
  if (!specSheetMap || !variant) return;
  /* Resolve the variant's registry handle (falling back to the product default for any
     variant beyond the 250-cap), then look up its URL in the uncapped registry map. */
  const handle =
    specSheetMap[String(variant.id)] ||
    specSheetMap[variant.id] ||
    specSheetMap.default;
  const url =
    handle && specRegistry && specRegistry[handle]
      ? String(specRegistry[handle])
      : '';
  const links = document.querySelectorAll(SEL.specSheetLink);
  const listItems = document.querySelectorAll(SEL.specSheetListItem);
  const messages = document.querySelectorAll(SEL.specSheetUnavailable);

  for (let i = 0; i < links.length; i++) {
    if (url) {
      links[i].setAttribute('href', url);
      links[i].hidden = false;
      links[i].removeAttribute('aria-hidden');
      links[i].removeAttribute('tabindex');
    } else {
      links[i].removeAttribute('href');
      links[i].hidden = true;
      links[i].setAttribute('aria-hidden', 'true');
      links[i].setAttribute('tabindex', '-1');
    }
  }

  for (let i = 0; i < messages.length; i++) {
    messages[i].hidden = Boolean(url);
    messages[i].textContent = url
      ? ''
      : 'Spec sheet unavailable for this variant';
  }

  for (let i = 0; i < listItems.length; i++) {
    listItems[i].hidden = !url;
  }
}

function updateInstallGuides() {
  // Bridge state: install guides stay static/shared — no mount-specific switching.
}

function formatNum(n, decimals) {
  const s = Number(n).toFixed(decimals);
  return s.replace(/\.?0+$/, '') || '0';
}

function updateSpecs(variant) {
  if (!variant) return;
  const weightEl = document.querySelector('[data-nco-spec-weight]');
  const dimEl = document.querySelector('[data-nco-spec-dimensions]');
  const capEl = document.querySelector('[data-nco-spec-capacity]');

  if (weightEl && variant.weightKg) {
    const kg = Number(variant.weightKg);
    const lbs = kg * 2.20462;
    weightEl.textContent = formatNum(kg, 1) + 'kg (' + formatNum(lbs, 2) + 'lbs)';
  }
  if (dimEl && variant.widthMm) {
    const w = Number(variant.widthMm);
    const h = Number(variant.heightMm);
    const wIn = w * 0.03937;
    const hIn = h * 0.03937;
    dimEl.textContent = 'D' + w + 'mm x H' + h + 'mm (D' + formatNum(wIn, 2) + 'in x H' + formatNum(hIn, 2) + 'in)';
  }
  if (capEl && variant.capacityL) {
    const litres = Number(variant.capacityL);
    const gal = litres * 0.264172;
    capEl.textContent = formatNum(litres, 1) + 'L (' + formatNum(gal, 2) + 'gal)';
  }
}

/* ------------------------------------------------------------------ */
/* Selection handlers                                                  */
/* ------------------------------------------------------------------ */

function selectColour(colourName, variantId) {
  state.colour = colourName;

  /* Update active swatch visual */
  const swatches = rootEl.querySelectorAll(SEL.colourSwatch);
  for (let i = 0; i < swatches.length; i++) {
    const isMatch =
      swatches[i].getAttribute('data-colour-name') === colourName;
    swatches[i].classList.toggle(CSS.swatchActive, isMatch);
    swatches[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }

  /* Update colour label */
  const label = rootEl.querySelector(SEL.colourLabel);
  if (label) {
    label.textContent = colourName;
  }

  const explicit = findVariantById(variantId);
  if (explicit && !state.finish) state.finish = explicit.finish || '';
  if (explicit && !state.sizeLabel) state.sizeLabel = explicit.sizeLabel || '';
  if (explicit && !state.toneMode) state.toneMode = explicit.toneMode || '';

  applyResolved();
  loadColourContent(colourName);
}

function selectMount(mountValue) {
  state.mount = mountValue;
  updateOptionButtons(SEL.mountOption, 'data-mount-value', mountValue);
  applyResolved();
}

function selectOverflow(overflowValue) {
  state.overflow = overflowValue;
  updateOptionButtons(
    SEL.overflowOption,
    'data-overflow-value',
    overflowValue
  );

  /* Overflow finish only applies when overflow is present. Hide + clear the
     finish dots for "No Overflow" so the two coupled selectors never resolve
     to an impossible combination (No Overflow + a finish). */
  const hasOverflow = overflowValue && overflowValue.toLowerCase().indexOf('no') !== 0;
  const ofGroup = rootEl.querySelector(SEL.overflowFinishGroup);
  if (ofGroup) {
    ofGroup.hidden = !hasOverflow;
  }
  if (!hasOverflow) {
    state.overflowFinish = '';
  } else if (!state.overflowFinish) {
    /* Overflow requires a finish to resolve a variant. Default to the first finish option
       so selecting "Overflow" immediately shows a valid finished variant rather than an
       incomplete (unresolvable) state. */
    const ofBtns = rootEl.querySelectorAll(SEL.overflowFinishOption);
    for (let k = 0; k < ofBtns.length; k++) {
      const fv = ofBtns[k].getAttribute('data-overflow-finish-value') || '';
      if (fv) {
        state.overflowFinish = fv;
        updateOptionButtons(SEL.overflowFinishOption, 'data-overflow-finish-value', fv);
        const ofLabelDef = rootEl.querySelector(SEL.overflowFinishLabel);
        if (ofLabelDef) ofLabelDef.textContent = fv;
        break;
      }
    }
  }

  applyResolved();
}

function selectOverflowFinish(overflowFinishValue) {
  state.overflowFinish = overflowFinishValue;
  updateOptionButtons(
    SEL.overflowFinishOption,
    'data-overflow-finish-value',
    overflowFinishValue
  );

  const ofLabel = rootEl.querySelector(SEL.overflowFinishLabel);
  if (ofLabel) ofLabel.textContent = overflowFinishValue;

  /* Choosing a finish implies overflow is present. */
  if (overflowFinishValue) {
    const ovBtns = rootEl.querySelectorAll(SEL.overflowOption);
    for (let i = 0; i < ovBtns.length; i++) {
      const val = ovBtns[i].getAttribute('data-overflow-value') || '';
      if (val && val.toLowerCase().indexOf('no') !== 0) {
        state.overflow = val;
        updateOptionButtons(SEL.overflowOption, 'data-overflow-value', val);
        break;
      }
    }
  }
  applyResolved();
}

function selectFinish(finishValue) {
  state.finish = finishValue;
  updateOptionButtons(SEL.finishOption, 'data-finish-value', finishValue);
  applyResolved();
}

function selectSize(sizeValue) {
  state.sizeLabel = sizeValue;
  updateOptionButtons(SEL.sizeOption, 'data-size-value', sizeValue);
  applyResolved();
}

function selectTone(toneValue) {
  state.toneMode = toneValue;
  updateOptionButtons(SEL.toneOption, 'data-tone-value', toneValue);

  const scGroup = rootEl.querySelector(SEL.secondaryColourGroup);
  if (scGroup) {
    scGroup.hidden = toneValue !== 'Two Tone';
  }

  if (toneValue !== 'Two Tone') {
    state.secondaryColour = '';
  }

  applyResolved();
}

function selectSecondaryColour(colourValue) {
  state.secondaryColour = colourValue;
  updateOptionButtons(
    SEL.secondaryColourOption,
    'data-secondary-colour-value',
    colourValue
  );

  const label = rootEl.querySelector(SEL.secondaryColourLabel);
  if (label) label.textContent = colourValue;

  applyResolved();
}

function selectTrayColour(colourValue) {
  state.trayColour = colourValue;
  updateOptionButtons(
    SEL.trayColourOption,
    'data-tray-colour-value',
    colourValue
  );

  const label = rootEl.querySelector(SEL.trayColourLabel);
  if (label) label.textContent = colourValue;

  applyResolved();
}

function selectTapHole(tapHoleValue) {
  state.tapHole = tapHoleValue;
  updateOptionButtons(
    SEL.tapholeOption,
    'data-taphole-value',
    tapHoleValue
  );
  applyResolved();
}

function getVariantSize(v) {
  if (v.sizeLabel) return v.sizeLabel;
  if (v.title) {
    const parts = v.title.split(' / ');
    if (parts.length >= 2) return parts[1].trim();
  }
  return '';
}

function updateTitleAndDescription(variant) {
  const titleEl = document.querySelector(SEL.productTitle);
  const descEl = document.querySelector(SEL.productDescription);

  if (titleEl) {
    let newTitle = (variant && variant.titleOverride) || '';
    if (!newTitle && variant) {
      const size = getVariantSize(variant);
      if (size && size.toLowerCase() !== 'standard') {
        newTitle = size + ' ' + productTitleFallback;
      }
    }
    if (!newTitle) newTitle = productTitleFallback;
    if (newTitle) titleEl.textContent = newTitle;
  }
  if (descEl) {
    const newDesc = (variant && variant.descriptionOverride) || productDescriptionFallback;
    if (newDesc) descEl.innerHTML = newDesc;
  }
}

function applyResolved() {
  const resolved = resolveVariant();
  const addBtn = document.querySelector(SEL.addToCartBtn);
  const unavailMsg = document.querySelector(SEL.unavailableMsg);

  if (!resolved || !resolved.available) {
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-disabled', 'true');
    }
    if (unavailMsg) {
      unavailMsg.hidden = false;
      unavailMsg.textContent = resolved
        ? 'This combination is currently unavailable.'
        : 'This combination is not available. Please adjust your selections.';
    }
    if (resolved) {
      updatePrice(resolved);
      updateSku(resolved);
      updateVariantId(resolved);
      updateSpecSheet(resolved);
      updateInstallGuides(resolved);
      updateTitleAndDescription(resolved);
      updateSpecs(resolved);
    } else {
      updateTitleAndDescription(null);
    }
    updateOptionAvailability();
    return;
  }

  if (addBtn) {
    addBtn.disabled = false;
    addBtn.removeAttribute('aria-disabled');
  }
  if (unavailMsg) unavailMsg.hidden = true;

  emitVariantChange(resolved);
  updatePrice(resolved);
  updateSku(resolved);
  updateAvailability(resolved);
  updateChips(resolved);
  updateVariantId(resolved);
  updateSpecSheet(resolved);
  updateInstallGuides(resolved);
  updateTitleAndDescription(resolved);
  updateSpecs(resolved);
  updateOptionAvailability();
}

/* ------------------------------------------------------------------ */
/* Event binding (delegation per S2)                                   */
/* ------------------------------------------------------------------ */

function bindEvents() {
  rootEl.addEventListener('click', function handleRootClick(e) {
    const swatch = e.target.closest(SEL.colourSwatch);
    if (swatch) {
      e.preventDefault();
      if (swatch.disabled || swatch.getAttribute('aria-disabled') === 'true') return;
      const colourName = swatch.getAttribute('data-colour-name') || '';
      const vid = parseInt(swatch.getAttribute('data-variant-id'), 10);
      selectColour(colourName, vid);
      return;
    }

    const mountBtn = e.target.closest(SEL.mountOption);
    if (mountBtn) {
      e.preventDefault();
      if (mountBtn.disabled || mountBtn.getAttribute('aria-disabled') === 'true') return;
      const mountVal = mountBtn.getAttribute('data-mount-value') || '';
      selectMount(mountVal);
      return;
    }

    const finishBtn = e.target.closest(SEL.finishOption);
    if (finishBtn) {
      e.preventDefault();
      if (finishBtn.disabled || finishBtn.getAttribute('aria-disabled') === 'true') return;
      selectFinish(finishBtn.getAttribute('data-finish-value') || '');
      return;
    }

    const sizeBtn = e.target.closest(SEL.sizeOption);
    if (sizeBtn) {
      e.preventDefault();
      if (sizeBtn.disabled || sizeBtn.getAttribute('aria-disabled') === 'true') return;
      selectSize(sizeBtn.getAttribute('data-size-value') || '');
      return;
    }

    const toneBtn = e.target.closest(SEL.toneOption);
    if (toneBtn) {
      e.preventDefault();
      if (toneBtn.disabled || toneBtn.getAttribute('aria-disabled') === 'true') return;
      selectTone(toneBtn.getAttribute('data-tone-value') || '');
      return;
    }

    const scBtn = e.target.closest(SEL.secondaryColourOption);
    if (scBtn) {
      e.preventDefault();
      if (scBtn.disabled || scBtn.getAttribute('aria-disabled') === 'true') return;
      selectSecondaryColour(scBtn.getAttribute('data-secondary-colour-value') || '');
      return;
    }

    const tcBtn = e.target.closest(SEL.trayColourOption);
    if (tcBtn) {
      e.preventDefault();
      if (tcBtn.disabled || tcBtn.getAttribute('aria-disabled') === 'true') return;
      selectTrayColour(tcBtn.getAttribute('data-tray-colour-value') || '');
      return;
    }

    const overflowBtn = e.target.closest(SEL.overflowOption);
    if (overflowBtn) {
      e.preventDefault();
      if (overflowBtn.disabled || overflowBtn.getAttribute('aria-disabled') === 'true') return;
      const ovVal = overflowBtn.getAttribute('data-overflow-value') || '';
      selectOverflow(ovVal);
      return;
    }

    const overflowFinishBtn = e.target.closest(SEL.overflowFinishOption);
    if (overflowFinishBtn) {
      e.preventDefault();
      if (overflowFinishBtn.disabled || overflowFinishBtn.getAttribute('aria-disabled') === 'true') return;
      const ofVal = overflowFinishBtn.getAttribute('data-overflow-finish-value') || '';
      selectOverflowFinish(ofVal);
      return;
    }

    const tapholeBtn = e.target.closest(SEL.tapholeOption);
    if (tapholeBtn) {
      e.preventDefault();
      if (tapholeBtn.disabled || tapholeBtn.getAttribute('aria-disabled') === 'true') return;
      const thVal = tapholeBtn.getAttribute('data-taphole-value') || '';
      selectTapHole(thVal);
      return;
    }
  });

  /* Keyboard: arrow keys within radio groups */
  rootEl.addEventListener('keydown', function handleKeydown(e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    const btn = e.target.closest('button[role="radio"]');
    if (!btn) return;

    const group = btn.closest('[role="radiogroup"]');
    if (!group) return;

    const buttons = Array.prototype.slice.call(
      group.querySelectorAll('button[role="radio"]')
    );
    const idx = buttons.indexOf(btn);
    if (idx === -1) return;

    e.preventDefault();
    const next =
      e.key === 'ArrowRight'
        ? buttons[(idx + 1) % buttons.length]
        : buttons[(idx - 1 + buttons.length) % buttons.length];
    next.focus();
    next.click();
  });
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

function init() {
  rootEl = document.querySelector(SEL.root);
  if (!rootEl) return;

  variantStore = parseJSON(SEL.variantData);
  colourContentStore = parseJSON(SEL.colourContent);
  specSheetMap = parseJSON(SEL.specSheetMap);
  specRegistry = parseJSON(SEL.specRegistry);
  hvIndex = parseJSON(SEL.variantIndex);
  hvColourImg = parseJSON(SEL.colourImageMap);
  colourHexMap = parseJSON(SEL.colourHexMap);
  const hvMeta = parseJSON(SEL.hvMeta);
  hvOptionNames = (hvMeta && hvMeta.optionNames) ? hvMeta.optionNames : null;
  hvConfigMap = (hvMeta && hvMeta.configMap) ? hvMeta.configMap : null;
  if (!variantStore || !variantStore.variants) return;

  productTitleFallback = variantStore.productTitle || '';
  productDescriptionFallback = variantStore.productDescription || '';

  /* Seed state from current variant, preferring the matrix default config:
     Standard size, Surface mount, Signature finish, No tap hole, No overflow,
     Single tone. Score same-colour variants and pick the most-default one. */
  let current = findVariantById(variantStore.currentVariantId);
  if (current) {
    const initColour = current.colourName || (current.title || '').split(' / ')[0];
    const defaultScore = function (v) {
      const s = (v.sizeLabel || getVariantSize(v) || '').toLowerCase();
      const t = (v.title || '').toLowerCase();
      const fin = (v.finish || '').toLowerCase();
      const th = (v.tapHole || '').toLowerCase();
      const ov = (v.overflow || '').toLowerCase();
      const tone = (v.toneMode || '').toLowerCase();
      let sc = 0;
      if (s === '' || s === 'standard') sc += 32;
      if (t.indexOf('wall') === -1) sc += 16;          /* prefer Surface */
      if (fin === '' || fin === 'signature') sc += 8;
      if (tone === '' || tone.indexOf('single') === 0 || tone.indexOf('one') === 0) sc += 4;
      if (th === '' || th.indexOf('no') === 0) sc += 2;
      if (ov === '' || ov.indexOf('no') === 0) sc += 1;
      return sc;
    };
    let best = current, bestScore = -1;
    for (let si = 0; si < variantStore.variants.length; si++) {
      const sv = variantStore.variants[si];
      if (sv.available === false) continue;
      const svColour = sv.colourName || (sv.title || '').split(' / ')[0];
      if (svColour !== initColour) continue;
      const sc = defaultScore(sv);
      if (sc > bestScore) { bestScore = sc; best = sv; }
    }
    current = best;
    state.colour = current.colourName || '';
    state.finish = current.finish || '';
    state.sizeLabel = current.sizeLabel || '';
    state.toneMode = current.toneMode || '';
    state.secondaryColour = current.secondaryColourName || '';
    state.trayColour = current.trayColourName || '';
    state.overflow = current.overflow || '';
    state.overflowFinish = current.overflowFinish || '';
    state.tapHole = current.tapHole || '';
  }

  /* Seed mount from first active mount button */
  const activeMount = rootEl.querySelector(
    SEL.mountOption + '.' + CSS.optionActive
  );
  if (activeMount) {
    state.mount = activeMount.getAttribute('data-mount-value') || '';
  }

  /* Default the tray to "No Tray" when that option exists (all cylinder colours have a
     No Tray variant). Read the literal from the button's data attribute so it matches the
     value the HV index is keyed by (option value), not the tray_colour_name metafield. */
  const noTrayBtn = rootEl.querySelector(
    '[data-nco-tray-colour-option][data-tray-colour-value="No Tray"]'
  );
  if (noTrayBtn) {
    state.trayColour = noTrayBtn.getAttribute('data-tray-colour-value');
    /* Keep the "Tray colour:" label in sync with the defaulted selection (the label is
       server-rendered from the default variant's tray metafield, which is a colour). */
    const trayLabel = rootEl.querySelector(SEL.trayColourLabel);
    if (trayLabel) trayLabel.textContent = state.trayColour;
  }

  /* Sync button active states with JS-seeded state */
  updateOptionButtons(SEL.sizeOption, 'data-size-value', state.sizeLabel);
  updateOptionButtons(SEL.mountOption, 'data-mount-value', state.mount);
  updateOptionButtons(SEL.finishOption, 'data-finish-value', state.finish);
  updateOptionButtons(SEL.toneOption, 'data-tone-value', state.toneMode);
  updateOptionButtons(SEL.trayColourOption, 'data-tray-colour-value', state.trayColour);
  updateOptionButtons(SEL.overflowOption, 'data-overflow-value', state.overflow);
  updateOptionButtons(SEL.overflowFinishOption, 'data-overflow-finish-value', state.overflowFinish);
  updateOptionButtons(SEL.tapholeOption, 'data-taphole-value', state.tapHole);

  /* Overflow finish only applies when overflow is present: hide on load if not */
  const initOfGroup = rootEl.querySelector(SEL.overflowFinishGroup);
  if (initOfGroup) {
    const initHasOverflow = state.overflow && state.overflow.toLowerCase().indexOf('no') !== 0;
    initOfGroup.hidden = !initHasOverflow;
  }

  /* Sync colour swatch active state */
  const swatches = rootEl.querySelectorAll(SEL.colourSwatch);
  for (let i = 0; i < swatches.length; i++) {
    const isMatch = swatches[i].getAttribute('data-colour-name') === state.colour;
    swatches[i].classList.toggle(CSS.swatchActive, isMatch);
    swatches[i].setAttribute('aria-checked', isMatch ? 'true' : 'false');
  }
  const colourLabel = rootEl.querySelector(SEL.colourLabel);
  if (colourLabel && state.colour) colourLabel.textContent = state.colour;

  /* Handle URL colour param */
  const urlColour = getURLParam('colour');
  if (urlColour) {
    const matchByURL = findVariantByColour(urlColour);
    if (matchByURL) {
      selectColour(matchByURL.colourName, matchByURL.id);
    } else if (hvIndex && hasColourControlValue(urlColour)) {
      /* High-variant products can have the requested colour beyond Liquid's
         250-variant cap. In that case, seed the colour directly and let
         variant_index resolve the complete option combination below. */
      selectColour(urlColour, null);
    }
  }

  /* Handle URL option params from collection filter cards */
  const urlMount = getURLParam('mount');
  if (urlMount && urlMount !== 'Freestanding') selectMount(urlMount);

  const urlSize = getURLParam('size');
  if (urlSize) selectSize(urlSize);

  const urlFinish = getURLParam('finish');
  if (urlFinish) selectFinish(urlFinish);

  const urlOverflow = getURLParam('overflow');
  if (urlOverflow) selectOverflow(urlOverflow);

  const urlTapHole = getURLParam('tapHole');
  if (urlTapHole) {
    let thVal = urlTapHole;
    if (thVal === '1 Tap Hole' || thVal === 'Single Tap Hole') thVal = 'Yes';
    else if (thVal === 'No Tap Hole') thVal = 'No';
    selectTapHole(thVal);
  }

  const urlOverflowFinish = getURLParam('overflowFinish');
  if (urlOverflowFinish) selectOverflowFinish(urlOverflowFinish);

  const urlTray = getURLParam('tray');
  if (urlTray) selectTrayColour(resolveTrayURLParam(urlTray));

  bindEvents();
  applySwatchHexes();
  updateOptionAvailability();
  applyResolved();

  /* Initial seeding complete. From here, selections swap the gallery image normally. */
  initialLoad = false;
}

/* ------------------------------------------------------------------ */
/* Bootstrap                                                           */
/* ------------------------------------------------------------------ */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
